import { useState, useEffect, useRef } from 'react'
import { CreatePlanModal } from './CreatePlanModal'

export function PlanSelectionPopup({
    item,
    selectedDate,
    plans,
    getItemPlans,
    onTogglePlan,
    onCreatePlan,
    onClose,
    anchorEl
}) {
    const [showCreateModal, setShowCreateModal] = useState(false)
    const popupRef = useRef(null)
    const itemPlans = getItemPlans(item.id, selectedDate)

    // Close popup when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    // Position popup near the anchor element (the button that was clicked)
    const getPopupStyle = () => {
        if (!anchorEl) return {}

        const rect = anchorEl.getBoundingClientRect()
        return {
            position: 'fixed',
            top: `${rect.bottom + 8}px`,
            left: `${rect.left}px`,
            zIndex: 99998,
        }
    }

    const handleTogglePlan = (planId) => {
        onTogglePlan(item, selectedDate, planId)
    }

    const handleCreateNew = () => {
        setShowCreateModal(true)
    }

    const handlePlanCreated = (name, color) => {
        const newPlan = onCreatePlan(name, color)
        setShowCreateModal(false)
        // Automatically add item to newly created plan
        onTogglePlan(item, selectedDate, newPlan.id)
    }

    return (
        <>
            {/* Popup */}
            <div
                ref={popupRef}
                style={getPopupStyle()}
                className="bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] max-w-[280px]"
            >
                <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">Add to Plan</p>
                </div>

                <div className="py-1 max-h-[300px] overflow-y-auto">
                    {plans.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                            No plans yet. Create one below!
                        </div>
                    ) : (
                        plans.map(plan => {
                            const isInThisPlan = itemPlans.includes(plan.id)

                            return (
                                <button
                                    key={plan.id}
                                    onClick={() => handleTogglePlan(plan.id)}
                                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                                >
                                    {/* Color indicator */}
                                    <div
                                        className="w-4 h-4 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: plan.color }}
                                    />

                                    {/* Plan name */}
                                    <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                                        {plan.name}
                                    </span>

                                    {/* Checkmark if in plan */}
                                    {isInThisPlan && (
                                        <svg
                                            className="w-5 h-5 text-green-600 flex-shrink-0"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    )}
                                </button>
                            )
                        })
                    )}
                </div>

                {/* Create new plan button */}
                <div className="border-t border-gray-100 pt-1">
                    <button
                        onClick={handleCreateNew}
                        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left"
                    >
                        <div className="w-4 h-4 flex items-center justify-center">
                            <svg
                                className="w-4 h-4 text-blue-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                        </div>
                        <span className="text-sm font-medium text-blue-600">
                            Create New Plan
                        </span>
                    </button>
                </div>
            </div>

            {/* Create Plan Modal */}
            {showCreateModal && (
                <CreatePlanModal
                    onClose={() => setShowCreateModal(false)}
                    onCreate={handlePlanCreated}
                />
            )}
        </>
    )
}
